import { useEffect, useState } from "react";
import { Box, Typography, Dialog } from "@mui/material";
import { T } from "./tokens";
import { LocalOnlyBadge } from "./ui";
import { toISODate } from "./data";

/**
 * Add, move or remove an ADMIN day on the Schedule. Holiday, Training and Sickness
 * sync from Youmanage and are read-only. PROTOTYPE: changes apply to this board.
 */

export interface DayEditTarget {
    consultantName: string;
    email: string;
    day: Date;
    existing: boolean; // true = editing an existing Admin day
}

const inputSx: React.CSSProperties = {
    border: "1px solid #DEE2EF",
    borderRadius: "7px",
    padding: "8px 10px",
    fontFamily: "'Nunito', 'Segoe UI', sans-serif",
    fontSize: "13px",
    color: "#150850",
    width: "100%",
    boxSizing: "border-box",
};

const btnSx = (variant: "dark" | "plain" | "danger") => ({
    padding: "9px 14px",
    borderRadius: "8px",
    fontSize: "12.5px",
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: T.font,
    border: variant === "dark" ? "none" : `1px solid ${variant === "danger" ? T.red : T.border}`,
    background: variant === "dark" ? T.ink : T.card,
    color: variant === "dark" ? "#fff" : variant === "danger" ? T.red : T.muted,
    "&:hover": { background: variant === "dark" ? T.indigoDeep : variant === "danger" ? "#FBF1EF" : T.rowTint },
});

const AdminDayModal = ({
    target,
    onClose,
    onApply,
}: {
    target: DayEditTarget | null;
    onClose: () => void;
    // newISO = date the admin day should sit on; null = remove it entirely.
    onApply: (email: string, originalISO: string, newISO: string | null, wasExisting: boolean) => void;
}) => {
    const [date, setDate] = useState("");

    useEffect(() => {
        if (target) setDate(toISODate(target.day));
    }, [target?.email, target?.day]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!target) return null;
    const originalISO = toISODate(target.day);
    const dateMoved = target.existing && date !== originalISO;

    const apply = () => {
        onApply(target.email, originalISO, date || originalISO, target.existing);
        onClose();
    };
    const remove = () => {
        onApply(target.email, originalISO, null, target.existing);
        onClose();
    };

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px", fontFamily: T.font, background: T.card } }}>
            <Box sx={{ padding: "20px 24px", fontFamily: T.font, color: T.ink2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "17px", fontWeight: 800, color: T.ink2 }}>
                        {target.existing ? "Edit admin day" : "Add admin day"}
                    </Typography>
                    <LocalOnlyBadge title="Applies to this board only" />
                </Box>
                <Typography sx={{ fontSize: "12.5px", color: T.muted, mt: "2px" }}>
                    {target.consultantName}
                </Typography>

                <Box sx={{ mt: "16px" }}>
                    <Typography sx={{ fontSize: "10.5px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: T.muted2, mb: "4px" }}>
                        Date
                    </Typography>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputSx} />
                </Box>

                <Box sx={{ display: "flex", gap: "10px", mt: "18px" }}>
                    {(!target.existing || dateMoved) && (
                        <Box component="button" onClick={apply} sx={btnSx("dark")}>
                            {target.existing ? "Update" : "Add admin day"}
                        </Box>
                    )}
                    {target.existing && (
                        <Box component="button" onClick={remove} sx={btnSx("danger")}>
                            Remove admin day
                        </Box>
                    )}
                    <Box sx={{ marginLeft: "auto" }}>
                        <Box component="button" onClick={onClose} sx={btnSx("plain")}>
                            Back
                        </Box>
                    </Box>
                </Box>

                <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "12px" }}>
                    Prototype only — changes apply to this board and are lost on refresh. Holiday, training
                    and sickness days sync from Youmanage/HR and are managed there.
                </Typography>
            </Box>
        </Dialog>
    );
};

export default AdminDayModal;
